import { Suspense, Component, type ReactNode, type ErrorInfo } from "react";

// Error boundary for chunk loading failures
class ChunkErrorBoundary extends Component<{ children: ReactNode }, { hasError: boolean }> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("Chunk load error:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-[50vh] flex flex-col items-center justify-center gap-4 p-8">
          <p className="text-[--text-secondary]">Failed to load page.</p>
          <button
            onClick={() => globalThis.location.reload()}
            className="px-4 py-2 bg-[--olive] text-white rounded-md hover:bg-[--olive-glow] transition-colors"
          >
            Reload
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

// Page wrapper with loading state and error boundary
export function PageLoader({ children }: Readonly<{ children: ReactNode }>) {
  return (
    <ChunkErrorBoundary>
      <Suspense fallback={<div className="min-h-[50vh]" />}>
        {children}
      </Suspense>
    </ChunkErrorBoundary>
  );
}
